import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Avatar,
  Typography,
  Box,
  Chip,
  Button
} from '@mui/material';
import { Business, Person } from '@mui/icons-material';
import { IAuth } from '../../commons/types/usuatios';
import { useTranslation } from '../../commons/useTranslasion';

interface DialogSelecaoUnidadeOperacionalProps {
  open: boolean;
  usuarios: IAuth[];
  onUsuarioSelect: (usuario: IAuth) => void;
  onCancel: () => void;
}

const DialogSelecaoUnidadeOperacional: React.FC<DialogSelecaoUnidadeOperacionalProps> = ({
  open,
  usuarios,
  onUsuarioSelect,
  onCancel
}) => {
  const { translate } = useTranslation();

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: '400px',
          background: 'linear-gradient(45deg, #64686e 0%, #c3cfe2 50%)'
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
          <Business sx={{ fontSize: 40, color: 'primary.main', mr: 1 }} />
          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            {translate('selecioneUnidadeOperacional') || 'Selecione a Unidade Operacional'}
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: '600px', mx: 'auto' }}>
          {translate('voceTemAcessoMultiplasUnidades') || 'Você tem acesso a múltiplas unidades operacionais. Selecione uma para continuar.'}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 0, pb: 3 }}>
        <Grid container spacing={3} sx={{ mt: 1, justifyContent: 'center' }}>
          {usuarios.map((usuario, index) => (
            <Grid item xs={12} sm={6} md={4} key={usuario.id || index}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'all 0.3s ease-in-out',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                    '& .card-content': {
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white'
                    },
                    '& .unit-name': {
                      color: 'white'
                    },
                    '& .user-info': {
                      color: 'rgba(255, 255, 255, 0.9)'
                    }
                  },
                  borderRadius: 2,
                  overflow: 'hidden'
                }}
              >
                <CardActionArea
                  onClick={() => onUsuarioSelect(usuario)}
                  sx={{ height: '100%' }}
                >
                  <CardContent 
                    className="card-content"
                    sx={{
                      height: '200px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      p: 3,
                      background: 'white',
                      transition: 'all 0.3s ease-in-out'
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        mb: 2,
                        bgcolor: 'primary.main',
                        fontSize: '1.5rem'
                      }}
                    >
                      <Business />
                    </Avatar>
                    
                    <Typography
                      variant="h6"
                      component="div"
                      className="unit-name"
                      sx={{
                        fontWeight: 'bold',
                        mb: 1,
                        color: 'text.primary',
                        transition: 'color 0.3s ease-in-out',
                        textAlign: 'center',
                        lineHeight: 1.2
                      }}
                    >
                      {(usuario as any).nomeUnidadeOperacional || 'Unidade Operacional'}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Person sx={{ fontSize: 16, mr: 0.5 }} />
                      <Typography
                        variant="body2"
                        className="user-info"
                        sx={{
                          color: 'text.secondary',
                          transition: 'color 0.3s ease-in-out'
                        }}
                      >
                        {usuario.nome}
                      </Typography>
                    </Box>
                    
                    <Typography
                      variant="caption"
                      className="user-info"
                      sx={{
                        color: 'text.secondary',
                        transition: 'color 0.3s ease-in-out',
                        mb: 1
                      }}
                    >
                      {usuario.email}
                    </Typography>

                    {(usuario as any).administrador && (
                      <Chip
                        label="Administrador"
                        size="small"
                        color="primary"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={onCancel}
          variant="outlined"
          size="large"
          sx={{
            minWidth: 120,
            borderRadius: 2
          }}
        >
          {translate('cancelar') || 'Cancelar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogSelecaoUnidadeOperacional;
